import React, { useContext, useRef, useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Header,
  Divider,
  Input,
  Form,
  Button,
  Grid
} from "semantic-ui-react";
import { RateStoreContext } from "../../stores/RateStore";
import "./GiveRate.css";
import Salary from "./Salary";
import { AppStoreContext } from "../../stores/AppStore";
import { GLOBAL_MODALS } from "../../stores/modals";
import { ROUTES } from "../../stores/routes";
import { trackAction } from "../../tracking";
import OnboardingActions from "../../tracking/actions";
import { useWindowSize } from "../../hooks/useWindowSize";
import { useTranslation } from "react-i18next";

const dividerStyle = { margin: "40px 0" };
const doneButtonStyle = { marginTop: 30 };

export default observer(function GiveRate() {
  const appStore = useContext(AppStoreContext);
  const rateStore = useContext(RateStoreContext);
  const { isSaving } = rateStore;
  const [t] = useTranslation();

  const windowSize = useWindowSize();
  const isWide = windowSize.width > 400;

  const currencySymbol = appStore.currency === "JPY" ? '¥' : '$';

  const onRateChange = (e, { value }) => {
    // only care about the numeric value
    rateStore.rateAmount = parseInt(value.replace(/[^\d]*/g, ""), 10) || 0;
    trackAction(OnboardingActions.RateChanged);
  };
  const submitRate = () => {
    trackAction(OnboardingActions.SubmitRate);
    rateStore
      .checkApproval()
      .then(() => {
        if (!rateStore.approvalNeeded) {
          appStore.globalModal = GLOBAL_MODALS.WHATS_NEXT;
        }
      })
      .catch(() => (appStore.globalModal = GLOBAL_MODALS.FATAL_ERROR));
  };

  const navigateBack = () => {
    trackAction(OnboardingActions.global.Click, {
      from: "rate",
      button: "back"
    });
    appStore.currentRoute = ROUTES.RESUME;
  };

  const recommended =
    rateStore.recommendationLoadingStatus === "loaded" &&
    rateStore.rangeLow !== 0;

  const rateInputRef = useRef();
  useEffect(
    () => {
      // focus the input field when we get our recommendation back
      // from the salary selector
      if (recommended) {
        rateInputRef.current.focus();
      }
    },
    [recommended]
  );

    //if we're proceeding through the app (vs refreshing on this page), this is returned after creating the new user
    if (appStore.country && !rateStore.recommendedRateByCountry && rateStore.shouldHideSalary(appStore.country, appStore.locale)) {
      rateStore.getRecommendedRateByCountry(appStore.country, appStore.currency);
    }
  return (
    <>
      <Header as="h2">{t("give_rate_header")}</Header>
      {rateStore.shouldHideSalary(appStore.country, appStore.locale) ?
        <p>{t("give_fixed_rate_body", { medianRate: `${currencySymbol}${rateStore.recommendedRateByCountryDisplay}` })}</p> :
        <>
          <p>{t("give_rate_body")}</p>
          <Salary />
        </>
      }

      <div>
        <Divider horizontal style={dividerStyle}>
          {t("my_hourly_rate")}
        </Divider>
        <div className="give-rate-container">
          <Grid textAlign="center">
            <Grid.Row>
                  <Form.Field className="give-rate-currency-field">
                    <Input
                      icon={appStore.currency === "JPY" ? "yen sign" : "dollar sign"}
                      iconPosition="left"
                      type="text"
                      value={rateStore.rateAmount}
                      onChange={onRateChange}
                      disabled={(!rateStore.salary || rateStore.isSaving) && !rateStore.shouldHideSalary(appStore.country, appStore.locale)}
                      ref={rateInputRef}
                    />
                  </Form.Field>
                <div className="give-rate-currency-container">
                  {appStore.currency === "USD" ? t("us_dollar_per_hour") : appStore.currency + ' ' + t("per_hour")}
                </div>
            </Grid.Row>
            <Grid.Row>
              <div>
                <small>{t("give_rate_fees_paid_info")}</small>
              </div>
            </Grid.Row>
          </Grid>
        </div>
      </div>

      <div style={{ overflow: "hidden" }}>
        <Button
          onClick={submitRate}
          loading={isSaving}
          disabled={rateStore.rateAmount === 0 || rateStore.isSaving}
          style={doneButtonStyle}
          primary
          floated="right"
          fluid={!isWide}
        >
          {t("save_done_button_label")}
        </Button>
        <Button
          onClick={navigateBack}
          floated="left"
          secondary
          fluid={!isWide}
          style={isWide ? doneButtonStyle : { marginTop: "1.5rem" }}
        >
          {t("back")}
        </Button>
      </div>
    </>
  );
});
